import axios from 'axios';

/*
 * authToken.js
 * ------------
 * Centralizes JWT handling for the admin UI.
 *
 * Storage: browser localStorage under key 'mnps_admin_token'.
 * (localStorage persists across tabs and reloads. We accept the XSS-risk
 * tradeoff for now since the admin panel is for internal use only.)
 *
 * Interceptor: auto-attaches `Authorization: Bearer <token>` to every
 * outgoing request, and on a 401 response clears the token and redirects
 * to /admin/login.
 */

const TOKEN_KEY = 'mnps_admin_token';
const USER_KEY = 'mnps_admin_user';

export const getToken = () => localStorage.getItem(TOKEN_KEY);

export const setToken = (token, user) => {
    localStorage.setItem(TOKEN_KEY, token);
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
};

export const clearToken = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
};

export const getCurrentUser = () => {
    const raw = localStorage.getItem(USER_KEY);
    try { return raw ? JSON.parse(raw) : null; } catch { return null; }
};

export const isLoggedIn = () => Boolean(getToken());

/*
 * Install axios interceptors. Call this ONCE at app start (from App.js
 * or index.js). Subsequent imports of this module won't re-install.
 */
let installed = false;
export const installAxiosAuth = () => {
    if (installed) return;
    installed = true;

    // Outgoing: attach token if present
    axios.interceptors.request.use((config) => {
        const token = getToken();
        if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    });

    // Incoming: on 401, clear and redirect
    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            if (error.response && error.response.status === 401) {
                clearToken();
                // Only redirect if we're on an admin page (don't disrupt public pages)
                if (window.location.pathname.startsWith('/admin') &&
                    window.location.pathname !== '/admin/login') {
                    window.location.href = '/admin/login';
                }
            }
            return Promise.reject(error);
        }
    );
};
